import { Effect, Predicate, Schema, Scope } from 'effect';
import { HttpClient, HttpClientError, HttpClientRequest } from 'effect/unstable/http';
import { HttpApiClient } from 'effect/unstable/httpapi';
import { OkraApi } from './api.js';
import { ApiNestedError, ApiStringError } from './common.js';
import { makeScopedHttpClient } from './client-transport.js';

export interface OkraClientOptions {
  /** Empty string means same-origin. */
  baseUrl: string;
  getToken?: () => Promise<string | null> | string | null;
  getHeaders?: () => Promise<Readonly<Record<string, string>>> | Readonly<Record<string, string>>;
  fetch?: typeof globalThis.fetch;
}

export class OkraClientError extends Schema.TaggedError<OkraClientError>()('OkraClientError', {
  message: Schema.String,
  status: Schema.optionalKey(Schema.Number),
  code: Schema.optionalKey(Schema.String),
  details: Schema.optionalKey(Schema.Unknown),
  nextActions: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        cmd: Schema.String,
        why: Schema.String,
      }),
    ),
  ),
}) {}

const isStringError = Schema.is(ApiStringError);
const isNestedError = Schema.is(ApiNestedError);

function statusForCode(code: string | undefined): number | undefined {
  if (!code) return undefined;
  if (code === 'unauthenticated' || code === 'unauthorized') return 401;
  if (code === 'file_not_found' || code === 'not_found') return 404;
  if (code === 'file_bytes_gone') return 410;
  if (code === 'inline_too_large' || code === 'page_limit_exceeded') return 413;
  if (code === 'unsupported_file_type') return 415;
  if (code === 'image_conversion_failed') return 422;
  if (code === 'parser_profile_not_executable') return 501;
  if (code.endsWith('_unavailable') || code.endsWith('_not_configured')) return 503;
  if (
    code.startsWith('invalid_') ||
    code === 'missing_file' ||
    code === 'missing_file_source' ||
    code === 'unknown_parser' ||
    code === 'parser_not_supported'
  )
    return 400;
  return undefined;
}

export function toOkraClientError(cause: unknown, fallback: string): OkraClientError {
  if (isStringError(cause)) {
    const code = cause.code ?? (cause.message !== undefined ? cause.error : undefined);
    return new OkraClientError({
      message: cause.message ?? cause.error,
      ...(statusForCode(code) !== undefined ? { status: statusForCode(code) } : {}),
      ...(code !== undefined ? { code } : {}),
      details: cause,
      ...(cause.next_actions !== undefined ? { nextActions: cause.next_actions } : {}),
    });
  }
  if (isNestedError(cause)) {
    const code = cause.code ?? cause.error.code;
    return new OkraClientError({
      message: cause.message ?? cause.error.message,
      ...(statusForCode(code) !== undefined ? { status: statusForCode(code) } : {}),
      ...(code !== undefined ? { code } : {}),
      details: cause,
      ...(cause.next_actions !== undefined ? { nextActions: cause.next_actions } : {}),
    });
  }
  if (HttpClientError.isHttpClientError(cause)) {
    return new OkraClientError({
      message: fallback,
      ...(cause.response !== undefined ? { status: cause.response.status } : {}),
    });
  }
  if (Predicate.isError(cause)) {
    return new OkraClientError({ message: fallback });
  }
  return new OkraClientError({ message: fallback });
}

function makeClient(headers: Readonly<Record<string, string>>, baseUrl: string) {
  return HttpApiClient.make(OkraApi, {
    ...(baseUrl === '' ? {} : { baseUrl }),
    ...(Object.keys(headers).length > 0
      ? {
          transformClient: (client: HttpClient.HttpClient) =>
            client.pipe(HttpClient.mapRequest(HttpClientRequest.setHeaders(headers))),
        }
      : {}),
  });
}

export type OkraEffectClient = Effect.Success<ReturnType<typeof makeClient>>;

/** Effect-native constructor for long-lived Effect programs. */
export const makeOkraEffectClient = Effect.fn('OkraEffectClient.make')(function* (options: {
  baseUrl: string;
  headers?: Readonly<Record<string, string>>;
}) {
  return yield* makeClient(options.headers ?? {}, options.baseUrl);
});

/** Native finite operation boundary for SDK and RTK Query clients. Resources cannot escape use. */
export function useOkraClientEffect<A>(
  options: OkraClientOptions,
  use: (client: OkraEffectClient) => Effect.Effect<A, unknown, never>,
  fallback: string,
): Effect.Effect<A, OkraClientError> {
  return Effect.gen(function* () {
    const lookup = <T>(get: () => T | Promise<T>) =>
      Effect.tryPromise({
        try: () => Promise.resolve(get()),
        catch: (cause) => toOkraClientError(cause, 'Authentication lookup failed'),
      });
    const [token, suppliedHeaders] = yield* Effect.all(
      [lookup(() => options.getToken?.() ?? null), lookup(() => options.getHeaders?.() ?? {})],
      { concurrency: 'unbounded' },
    );
    const headers = token
      ? { ...suppliedHeaders, Authorization: `Bearer ${token}` }
      : suppliedHeaders;
    const scope = yield* Scope.Scope;
    return yield* makeClient(headers, options.baseUrl).pipe(
      Effect.flatMap(use),
      Effect.provideService(
        HttpClient.HttpClient,
        makeScopedHttpClient(options.fetch ?? globalThis.fetch, scope),
      ),
    );
  }).pipe(
    Effect.scoped,
    Effect.mapError((cause) =>
      cause instanceof OkraClientError ? cause : toOkraClientError(cause, fallback),
    ),
  );
}

/** Promise bridge for SDK and RTK Query boundaries. */
export function runOkraClient<A>(
  options: OkraClientOptions,
  use: (client: OkraEffectClient) => Effect.Effect<A, unknown, never>,
  fallback: string,
  signal?: AbortSignal,
): Promise<A> {
  return Effect.runPromise(
    Effect.suspend(() =>
      signal?.aborted ? Effect.interrupt : useOkraClientEffect(options, use, fallback),
    ),
    signal ? { signal } : undefined,
  );
}
